import { Component, ChangeDetectionStrategy, OnInit, OnDestroy } from '@angular/core';
import { FormGroup, FormControl, ValidatorFn, AbstractControl } from '@angular/forms';
import { Router } from '@angular/router';
import { RoutesCommon } from '@conf';
import { AswValidators } from '@lib/helpers';
import {
  DynamicContent,
  PageType,
  ValidateOptions,
  ValidationErrorInfo,
  DialogClickType,
  DialogType,
  ErrorType,
  LogCustomMsg,
  LogType,
  DialogSize,
} from '@lib/interfaces';
import {
  DialogDisplayService,
  DynamicContentService,
  AswCommonStoreService,
  ErrorsHandlerService,
  LoggerDatadogService,
  AswContextStoreService,
  PageInitService,
} from '@lib/services';
import { Subscription, Observable, combineLatest, timer } from 'rxjs';
import { delay, map, take } from 'rxjs/operators';

@Component({
  selector: 'asw-test',
  templateUrl: './test.component.html',
  styleUrls: ['./test.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestComponent implements OnInit, OnDestroy {
  private _subscription: Subscription = new Subscription();
  private _functionId = 'Hello';
  private _pageId = 'World';

  // input / input group
  public inputFG!: FormGroup;
  public inputGroup_from!: FormControl;
  public inputGroup_to!: FormControl;
  // select / selectDateYm
  public selectFC!: FormControl;
  public selectFC_disable!: FormControl;
  public selectYmControl!: FormControl;
  // radio button
  public radiobuttonFC!: FormControl;
  public radiobuttonFC_2!: FormControl;
  // checkbox / checkbox group
  public checkBoxFC!: FormControl;
  public checkBoxFC_2!: FormControl;
  public checkBoxGroupFC_1!: FormControl;
  public checkBoxGroupFC_2!: FormControl;
  public checkBoxGroupFC_3!: FormControl;
  // range slider
  public rangeSliderFC!: FormControl;
  public rangeSliderFC_2!: FormControl;
  public rangeSliderFC_3!: FormControl;
  // toggle switch
  public switchControl!: FormControl;

  // 動的文言データ
  public dynamicData$?: Observable<DynamicContent | null>;

  constructor(
    private _dialogSvc: DialogDisplayService,
    private _dynamicSvc: DynamicContentService,
    private _aswCommonSvc: AswCommonStoreService,
    private _errorsHandlerSvc: ErrorsHandlerService,
    private _loggerSvc: LoggerDatadogService,
    private _aswContextSvc: AswContextStoreService,
    private _router: Router,
    private _pageInitSvc: PageInitService
  ) {
    // input
    this.inputFG = new FormGroup({
      inputFC: new FormControl('', [
        AswValidators.required({ params: { key: 0, value: 'label_input' } }),
        AswValidators.lengths({
          min: 3,
          params: [
            { key: 0, value: 'label_input' },
            { key: 1, value: 3 },
          ],
        }),
      ]),
      inputFC_2: new FormControl('', [
        AswValidators.required({
          errorMsgId: 'validationError_inputCustom',
          params: { key: 0, value: 'label_input' },
        }),
        AswValidators.phoneNumber({
          telCountryCode: '81',
          isSmsSend: true,
          params: { key: 0, value: 'label_input' },
        }),
      ]),
    });

    // input group
    this.inputGroup_from = new FormControl(
      '',
      AswValidators.required({ params: { key: 0, value: 'from', dontTranslate: true } })
    );
    this.inputGroup_to = new FormControl(
      '',
      AswValidators.required({ params: { key: 0, value: 'to', dontTranslate: true } })
    );

    // select
    this.selectFC = new FormControl(
      'option1',
      AswValidators.required({ isNotInput: true, params: { key: 0, value: 'label_select' } })
    );
    this.selectFC_disable = new FormControl({ value: 'option1', disabled: true });
    // selectDateYm
    this.selectYmControl = new FormControl(
      '202308',
      this._validateSelectYm({ params: { key: 0, value: 'label_selectYm' } })
    );

    // radio group
    this.radiobuttonFC = new FormControl(
      '',
      AswValidators.required({ isNotInput: true, params: { key: 0, value: 'label_radio' } })
    );
    this.radiobuttonFC_2 = new FormControl('3');

    // checkbox group
    this.checkBoxFC = new FormControl(
      false,
      AswValidators.required({ isNotInput: true, params: { key: 0, value: 'label_checkbox' } })
    );
    this.checkBoxFC_2 = new FormControl(
      false,
      AswValidators.required({ isNotInput: true, params: { key: 0, value: 'label_checkbox' } })
    );
    this.checkBoxGroupFC_1 = new FormControl(
      '',
      AswValidators.required({ isNotInput: true, params: { key: 0, value: 'label_checkbox' } })
    );
    this.checkBoxGroupFC_2 = new FormControl([false, false, true, false, false]);
    this.checkBoxGroupFC_3 = new FormControl([false, false, true, false, false]);

    // range slider
    this.rangeSliderFC = new FormControl();
    this.rangeSliderFC_2 = new FormControl();
    this.rangeSliderFC_3 = new FormControl();

    // toggle switch
    this.switchControl = new FormControl(true);
  }

  /**
   * ngOnInit
   */
  public ngOnInit(): void {
    // AswCommon Store更新
    this._aswCommonSvc.updateAswCommon({
      functionId: this._functionId,
      // subFunctionId: 'SUB01',
      pageId: this._pageId,
      // subPageId: 'M010',
    });

    // 初期化開始
    this._pageInitSvc.startInit();

    // AswContext Store更新
    this._aswContextSvc.updateAswContext({
      lang: 'ja',
      currencyCode: 'JPY',
      deviceType: 'PC',
      pointOfSaleId: 'TESTOFFICE',
    });

    this._subscription.add(
      combineLatest([this._aswCommonSvc.getFunctionId$(), this._aswCommonSvc.getPageId$()]).subscribe(([p, f]) => {
        console.log(`## FunctionId:${p} ## PageId: ${f}`);
      })
    );

    const dynamicParams$ = combineLatest([
      this._aswCommonSvc.getFunctionId$(), // API store結果などをセットする想定
      this._aswCommonSvc.getPageId$(),
    ]).pipe(
      map(([a, b]) => {
        return {
          key1: a,
          key2: b,
        };
      })
    );
    // 初期化終了
    timer(3000)
      .pipe(take(1))
      .subscribe(() => this._pageInitSvc.endInit(dynamicParams$));

    // 動的文言取得
    this.dynamicData$ = this._dynamicSvc.getDynamicContent$();
  }

  /**
   * 年月プルダウンValidation
   *
   * @param option
   * @returns
   */
  private _validateSelectYm(option?: ValidateOptions): ValidatorFn {
    return (control: AbstractControl): { [key: string]: ValidationErrorInfo } | null => {
      if (control.value && control.value.length === 6) {
        return null;
      } else {
        return {
          'validate-required-selectYm': {
            errorMsgId: option?.errorMsgId || 'validationError_selectDateYm',
            params: option?.params,
          },
        };
      }
    };
  }

  /**
   * submit時のvalidation check test
   */
  public testFormValidateSubmit() {
    if (!this.inputFG.valid) {
      this.inputFG.markAllAsTouched();
      this.submitFormValidate();
    }
  }

  /**
   * 確認ダイアログテスト（選択ダイアログ）
   */
  public openDialogChoice() {
    this._dialogSvc.openDialog({ message: 'test.dialog.message.choice' }).buttonClick$.subscribe((result) => {
      if (result.clickType === DialogClickType.CONFIRM) {
        console.log('## DialogChoice: yes');
      } else if (result.clickType === DialogClickType.CLOSE) {
        console.log('## DialogChoice: no');
      }
    });
  }

  /**
   * 確認ダイアログテスト（警告ダイアログ）
   */
  public openDialogWarn() {
    this._dialogSvc
      .openDialog({
        size: DialogSize.L, // size: large
        type: DialogType.WARN,
        message: 'test.dialog.message.warn',
        // 埋め込み文字付き
        messageParams: [
          { key: 0, value: 3, dontTranslate: true },
          { key: 1, value: 'test_dialog_param1' },
          { key: 2, value: 'test_dialog_param2' },
        ],
      })
      .buttonClick$.subscribe((result) => {
        if (result.clickType === DialogClickType.CONFIRM) {
          console.log('## DialogWarn: yes');
        } else if (result.clickType === DialogClickType.CLOSE) {
          console.log('## DialogWarn: no');
        }
      });
  }

  public submitFormValidate() {
    this._errorsHandlerSvc.setRetryableError(PageType.PAGE);
  }

  /**
   * エラーテスト（継続可能エラー）
   */
  public setRetryableError() {
    this._errorsHandlerSvc.setRetryableError(PageType.PAGE, {
      errorMsgId: 'retryableE0001',
      apiErrorCode: 'EAPIRetryable01',
    });
  }

  /**
   * エラーテスト（継続不可能エラー - businessLogic）
   */
  public setNotReBusinessLogicError() {
    this._errorsHandlerSvc.setNotRetryableError({
      errorType: ErrorType.BUSINESS_LOGIC,
      errorMsgId: 'notRetryableE0001',
      apiErrorCode: 'EAPINotRetryable01',
    });
  }

  /**
   * エラーテスト（継続不可能エラー - system）
   */
  public setNotReSystemError() {
    this._errorsHandlerSvc.setNotRetryableError({
      errorType: ErrorType.SYSTEM,
      apiErrorCode: 'EAPINotRetryable02',
    });
  }

  /**
   * エラーテスト（継続不可能エラー - sessionTimeout）
   */
  public setNotReSessionTimeoutError() {
    this._errorsHandlerSvc.setNotRetryableError({
      errorType: ErrorType.SESSION_TIMEOUT,
      apiErrorCode: 'EAPINotRetryable03',
    });
  }

  /**
   * エラーテスト（継続不可能エラー - browserBack）
   */
  public setNotReBrowserBackError() {
    this._router.navigate([RoutesCommon.BROWSER_BACK_ERROR]);
  }

  /**
   * 動的文言条件変更テスト
   */
  public changeDynamicCondition() {
    this._aswContextSvc.updateAswContext({
      loginStatus: 'REAL_LOGIN',
    });
  }

  /**
   * ログ出力テスト
   */
  public sendLogWithMask() {
    const sendTest: LogCustomMsg = {
      key1: {
        key1_1: 'key1_1_Value',
        key1_2: 'key1_2_Value',
      },
      key2: 'key2Value',
      key3: {
        mebrPswd: 'abcd1234',
        webPswd: 'abcd1234',
      },
      cardNumber: '123456789',
      cvv: '111',
    };
    this._loggerSvc.info(LogType.PAGE_VIEW, sendTest, true);
  }

  /**
   * 多重送信テスト
   */
  public sendMultiple() {
    console.log(`[${new Date()}] sendMultiple`);
  }

  public ngOnDestroy() {
    this._subscription.unsubscribe();
    this._errorsHandlerSvc.clearRetryableError(PageType.PAGE);
    this._dynamicSvc.clearDynamicContent();
    this._pageInitSvc.pageInitUnsubscribe();
  }
}
